import { BaseService } from '@common/http-layer/service/base.service'
import { log } from '@common/core-layer/services/logger.service'

export interface SignedUrlResponse {
  signedUrl: string
  url: string
}

export interface SignedUrlParams {
  name: string
  folder: string
  type: string
  locationId: string
  groupId: string
}

export interface UploadPayload {
  file: File
  filename: string
  folder: string
  type: string
  size: number
  locationId: string
  groupId: string
  progressUpdate?: (progress: number) => void
}

export interface UploadMediaResponse {
  id: string
  url: string
  name: string
  type: string
  size: number
  status: string
}

/**
 * Media service - handles all media upload related API calls
 * Base URL: REST_API_URLS (or fallback to local API)
 * Base Path: /api/media
 */
export class MediaService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
    })
  }

  /**
   * Generate signed URL for direct upload to cloud storage
   */
  async generateSignedUrl(params: SignedUrlParams): Promise<SignedUrlResponse> {
    try {
      // Prepare query parameters
      const queryParams = {
        groupId: params.groupId,
      }

      // Prepare request body (payload)
      const requestBody = {
        folder: params.folder,
        name: params.name,
        type: params.type,
      }

      const { data } = await this.post<SignedUrlResponse>(`/${params.locationId}/medias/signed-url`, requestBody, { params: queryParams })

      return data
    } catch (error) {
      log.error('Error generating signed URL', {
        error: error instanceof Error ? error : undefined,
        payload: { folder: params.folder, name: params.name, type: params.type },
      })
      throw error
    }
  }

  /**
   * Upload file using two-step process: get signed URL then upload directly to cloud storage
   */
  async uploadFile(payload: UploadPayload): Promise<{
    uploadPromise: Promise<UploadMediaResponse>
    cancelUpload: () => void
  }> {
    const filename = payload.filename || this.generateUniqueId()

    log.info('Starting media upload', {
      payload: {
        folder: payload.folder,
        filename,
        type: payload.type,
        size: payload.size,
      },
    })

    try {
      // Get signed URL for direct upload using BaseService
      const { signedUrl, url } = await this.generateSignedUrl({
        name: filename,
        folder: payload.folder,
        type: payload.type,
        locationId: payload.locationId,
        groupId: payload.groupId,
      })

      const xhr = new XMLHttpRequest()

      const uploadPromise: Promise<UploadMediaResponse> = new Promise((resolve, reject) => {
        // Configure XHR for direct cloud storage upload
        xhr.open('PUT', signedUrl, true)
        // xhr.setRequestHeader('Content-Type', payload.type)

        // Progress tracking
        if (payload.progressUpdate) {
          xhr.upload.onprogress = event => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100)
              payload.progressUpdate!(progress)
            }
          }
        }

        // Success handler
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve({
              id: this.generateUniqueId(),
              url: url,
              name: filename,
              type: payload.type,
              size: payload.size,
              status: 'completed',
            })
          } else {
            log.error('Media upload failed', {
              payload: {
                folder: payload.folder,
                filename,
                status: xhr.status,
                statusText: xhr.statusText,
              },
            })

            // If it's a mock URL and we get 404, treat as development fallback
            if (this.isDevelopmentMode() && signedUrl.includes('mock-storage.example.com')) {
              resolve({
                id: this.generateUniqueId(),
                url: url,
                name: filename,
                type: payload.type,
                size: payload.size,
                status: 'completed',
              })
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`))
            }
          }
        }

        // Error handlers
        xhr.onerror = () => {
          log.error('Network error during media upload', {
            payload: { folder: payload.folder, filename },
          })
          reject(new Error('Upload failed due to network error'))
        }

        xhr.onabort = () => {
          log.warn('Media upload cancelled', {
            payload: { folder: payload.folder, filename },
          })
          reject(new Error('Upload cancelled'))
        }

        // Complete handler
        xhr.onloadend = () => {
          if (payload.progressUpdate) {
            payload.progressUpdate(100)
          }
        }

        // Send file
        xhr.send(payload.file)
      })

      const cancelUpload = () => {
        xhr.abort()
      }

      return { uploadPromise, cancelUpload }
    } catch (error) {
      log.error('Error in media upload process', {
        error: error instanceof Error ? error : undefined,
        payload: { folder: payload.folder, filename, type: payload.type },
      })

      if (this.isDevelopmentMode()) {
        return this.createDevelopmentUpload(payload)
      }

      throw error
    }
  }

  /**
   * Create development upload fallback when signed URL fails
   */
  private createDevelopmentUpload(payload: UploadPayload): {
    uploadPromise: Promise<UploadMediaResponse>
    cancelUpload: () => void
  } {
    let cancelled = false

    const uploadPromise: Promise<UploadMediaResponse> = new Promise((resolve, reject) => {
      const simulateProgress = async () => {
        for (let progress = 0; progress <= 100; progress += 20) {
          if (cancelled) {
            console.log('🛑 [MediaService.createDevelopmentUpload] Upload cancelled during simulation')
            reject(new Error('Upload cancelled'))
            return
          }

          if (payload.progressUpdate) {
            payload.progressUpdate(progress)
          }

          await new Promise(resolve => setTimeout(resolve, 200))
        }

        if (!cancelled) {
          const mockResponse: UploadMediaResponse = {
            id: this.generateUniqueId(),
            url: this.generateMockUrl(payload.filename, payload.folder),
            name: payload.filename,
            type: payload.type,
            size: payload.size,
            status: 'completed',
          }

          resolve(mockResponse)
        }
      }

      simulateProgress()
    })

    const cancelUpload = () => {
      cancelled = true
    }

    return { uploadPromise, cancelUpload }
  }

  /**
   * Generate mock URL for development mode
   */
  private generateMockUrl(filename: string, folder: string): string {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'
    const uniqueId = this.generateUniqueId()
    const extension = filename.split('.').pop()
    const mockUrl = `${baseUrl}/uploads/${folder}/${uniqueId}.${extension}`

    return mockUrl
  }

  /**
   * Check if running in development mode
   */
  private isDevelopmentMode(): boolean {
    return process.env.NUXT_ENV === 'development' || (typeof window !== 'undefined' && window.location.hostname === 'localhost')
  }

  /**
   * Generate unique ID for uploads
   */
  private generateUniqueId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2)
  }
}

// Export singleton instance
export default new MediaService()
